import { useEffect, useRef } from "react";
import { Platform } from "@xt/xtbase";

let reuseTitle = null;
let reuseHandler = null;
let reusing = false;

/**
 * Update App native nav title only (does not touch document.title / browser tab).
 * @param {string} title Localized page title string (not an i18n key).
 */
export function setAppNativeTitle(title) {
  if (typeof window === "undefined" || !title) return;

  try {
    window.dsBridge?.call?.("setTitle", title);
  } catch (error) {
    console.warn("[H5 title] setTitle bridge unavailable", error);
  }
}

/** @deprecated Use setAppNativeTitle — kept for internal reuse callbacks. */
export function syncNativeWebViewTitle(title) {
  if (Platform.isInApp()) {
    setAppNativeTitle(title);
  }
}

function installGlobalReuseHandler() {
  if (typeof window === "undefined") return;

  if (!reuseTitle && !reuseHandler) {
    if (window.xtOnWebviewReuse) {
      delete window.xtOnWebviewReuse;
    }
    return;
  }

  window.xtOnWebviewReuse = async function xtOnWebviewReuse() {
    if (reusing) return;
    reusing = true;
    try {
      if (reuseTitle) {
        setAppNativeTitle(reuseTitle);
      }
      if (reuseHandler) {
        await reuseHandler();
      }
      // Re-assert after async refresh — DOM updates must not leave native title stale.
      if (reuseTitle) {
        setAppNativeTitle(reuseTitle);
      }
      if (typeof window.scrollTo === "function") {
        window.scrollTo(0, 0);
      }
    } finally {
      reusing = false;
    }
  };
}

/**
 * Register the current page title for WebView reuse (iOS cached WebView).
 * @param {string} title
 * @returns {() => void} cleanup
 */
export function setAppWebviewReuseTitle(title) {
  if (typeof window === "undefined") return () => {};

  reuseTitle = title || null;
  if (title) {
    setAppNativeTitle(title);
  }
  installGlobalReuseHandler();

  return () => {
    if (reuseTitle === title) {
      reuseTitle = null;
    }
    installGlobalReuseHandler();
  };
}

/**
 * Register a global handler for App webview reuse (window.xtOnWebviewReuse).
 * The App calls this when a cached H5 page is shown again.
 */
export function registerAppWebviewReuse(handler) {
  if (typeof window === "undefined") return () => {};

  reuseHandler = handler;
  installGlobalReuseHandler();

  return () => {
    if (reuseHandler === handler) {
      reuseHandler = null;
    }
    installGlobalReuseHandler();
  };
}

/** React hook — only active inside the App webview when enabled. */
export function useAppWebviewReuse(handler, enabled = true) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled || !Platform.isInApp()) return undefined;
    return registerAppWebviewReuse(async () => handlerRef.current());
  }, [enabled]);
}
